// src/store/cartStore.ts
import { create } from 'zustand'
import { persist, PersistStorage } from 'zustand/middleware' // Importe PersistStorage
import { Product } from '../types/product'

interface CartItem {
  product: Product
  quantity: number
}

interface CartState {
  items: CartItem[]
  addItem: (product: Product) => void
  removeItem: (productId: number) => void
  increaseQuantity: (productId: number) => void
  decreaseQuantity: (productId: number) => void
  clearCart: () => void
  getTotalItems: () => number
  getTotalPrice: () => number
}

const localStoragePersist: PersistStorage<CartState> = {
  getItem: (name) => {
    const str = localStorage.getItem(name)
    return str === null ? null : JSON.parse(str)
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value))
  },
  removeItem: (name) => {
    localStorage.removeItem(name)
  },
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (product) => {
        set((state) => {
          const existingItem = state.items.find((item) => item.product.id === product.id)
          if (existingItem) {
            return {
              items: state.items.map((item) =>
                item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
              ),
            }
          } else {
            return { items: [...state.items, { product, quantity: 1 }] }
          }
        })
      },
      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.product.id !== productId),
        }))
      },
      increaseQuantity: (productId) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId ? { ...item, quantity: item.quantity + 1 } : item
          ),
        }))
      },
      decreaseQuantity: (productId) => {
        set((state) => ({
          items: state.items
            .map((item) => {
              if (item.product.id === productId) {
                const newQuantity = item.quantity - 1
                return newQuantity > 0 ? { ...item, quantity: newQuantity } : null
              }
              return item
            })
            .filter(Boolean) as CartItem[], // Remove itens com quantidade zero
        }))
      },
      clearCart: () => {
        set({ items: [] })
      },
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0)
      },
      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + item.product.price * item.quantity, 0)
      },
    }),
    {
      name: 'cart-storage', // Nome da chave no localStorage
      storage: localStoragePersist, // Usar a implementação correta do localStorage
    }
  )
)
